// 🧮 MELD-Na — escore de ALOCAÇÃO VIGENTE em SP (B358-FU2, AI_156)
// ─────────────────────────────────────────────────────────────
// O sistema nasceu calculando só o MELD 3.0 (o da Portaria 8.041), mas a 8.041
// segue em vacatio (prorrogada até ~31/12/2026) e a Central de SP aloca pelo
// MELD-Na. Este módulo calcula o MELD-Na dos componentes do exame para a IA
// aplicar as regras vigentes (MELD mínimo ≤ 14, validade do exame, MELD > 10 da
// irressecabilidade do CHC). ⟳ REVISAR quando a 8.041 entrar em vigor.
//
// Fórmula OPTN/UNOS (2016):
//   - creatinina, bilirrubina e INR < 1,0 → 1,0; creatinina > 4,0 (ou diálise
//     ≥ 2×/semana) → 4,0
//   - MELD(i) = 0,957·ln(Cr) + 0,378·ln(BT) + 1,120·ln(INR) + 0,643,
//     arredondado à 1ª casa e × 10
//   - sódio limitado a 125–137; se MELD(i) > 11:
//     MELD-Na = MELD(i) + 1,32·(137 − Na) − 0,033·MELD(i)·(137 − Na)
//   - arredondado para inteiro, teto 40
// ─────────────────────────────────────────────────────────────

export interface ComponentesMeldNa {
  creatinina: number;
  bilirrubina: number;
  inr: number;
  sodio: number;
  /** Diálise ≥ 2×/semana na última semana → creatinina = 4,0. */
  emDialise?: boolean;
}

const num = (v: unknown): number => {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v.replace(",", "."));
  return NaN;
};

/** MELD-Na pela fórmula OPTN/UNOS. Devolve null se faltar componente válido. */
export function calcularMeldNa(c: ComponentesMeldNa): number | null {
  const vals = [c.creatinina, c.bilirrubina, c.inr, c.sodio];
  if (vals.some((v) => !Number.isFinite(v) || v <= 0)) return null;
  const cr = c.emDialise ? 4 : Math.min(Math.max(c.creatinina, 1), 4);
  const bt = Math.max(c.bilirrubina, 1);
  const inr = Math.max(c.inr, 1);
  const bruto =
    0.957 * Math.log(cr) + 0.378 * Math.log(bt) + 1.12 * Math.log(inr) + 0.643;
  const meldI = Math.round(bruto * 10);
  if (meldI <= 11) return Math.min(meldI, 40);
  const na = Math.min(Math.max(c.sodio, 125), 137);
  const meldNa = meldI + 1.32 * (137 - na) - 0.033 * meldI * (137 - na);
  return Math.min(Math.round(meldNa), 40);
}

export interface MeldNaDeExame {
  valor: number;
  data: string;
}

/**
 * MELD-Na do exame MAIS RECENTE que tenha os 4 componentes. `exames` em ordem
 * ASCENDENTE (como sai de anonimizarDados) — percorre do fim para o começo.
 */
export function meldNaDoExameMaisRecente(
  exames: Array<{ data?: string; dataColeta?: string; valores?: Record<string, unknown> }> | undefined,
  emDialise?: boolean
): MeldNaDeExame | null {
  if (!Array.isArray(exames)) return null;
  for (let i = exames.length - 1; i >= 0; i--) {
    const v = exames[i]?.valores || {};
    const valor = calcularMeldNa({
      creatinina: num(v.Creatinina),
      bilirrubina: num(v.BT),
      inr: num(v.INR),
      sodio: num(v.Sodio),
      emDialise,
    });
    if (valor !== null) {
      return { valor, data: exames[i].data || exames[i].dataColeta || "" };
    }
  }
  return null;
}
